import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import { Col, Form, FormFeedback, FormGroup, Label, Row, Spinner } from "reactstrap";
import { Alert, Badge, Button, Checkbox, Divider, Text } from "@mantine/core";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import Swal from "sweetalert2";
import * as yup from "yup";

import { ThemedButton } from "@/components/ThemedButton";

import { useCertCheckout } from "@/reducers/certCheckoutSlice";

import generalApi from "@/services/general-api";
import { ApplePayFlow } from "services/payments/ApplePay";
import { CreditCardFlow } from "services/payments/CreditCardPay";
import { GooglePayFlow } from "services/payments/GooglePay";
import { PayNowFlow } from "services/payments/PayNow";

import { getErrorMessage } from "utilities/error";
import { yupHelper } from "utilities/yupHelpers";

import CreditCardLogo from "assets/img/endorse/cc-logo.png";
import PayNowLogo from "assets/img/endorse/paynow-logo.png";

import { ReactComponent as FolderOffSvg } from "../assets/folder-off.svg";
import { clearCachedFormData, getCachedFormData, setCachedFormData } from "./checkoutCache";
import { CheckoutSidebar } from "./CheckoutSidebar";
import { PreviewImage } from "./components/PreviewImage";
import { TNC } from "./components/TNC";
import styles from "./EventCheckoutSummaryPage.module.scss";

const CERT_GREEN_COLOR = "#00AB84";

// Session storage key for caching form data
export const ProfessionalCertCheckoutSummaryPage = () => {
	const { checkoutInfo } = useCertCheckout();
	const [accordionOpen, setAccordionOpen] = useState(true);
	const [paymentmethod, setPaymentmethod] = useState("paynow");
	const [selectedFolder, setSelectedFolder] = useState(null);
	const [selectedCollectionLocation, setSelectedCollectionLocation] = useState(null);
	let navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const cert_request_id = searchParams.get("id");

	const schema = useMemo(() => {
		const baseSchema = {
			cert_type: yup.string().required(),
			cert_folder_selection: yup.string().nullable(),
			cert_delivery_method: yup.string().when(["cert_type"], {
				is: (cert_type) => cert_type === "physical",
				then: (schema) => schema.required("Please select a delivery method"),
			}),
			self_collection_location: yup.string().when(["cert_delivery_method"], {
				is: (cert_delivery_method) => cert_delivery_method === "Self Collection",
				then: (schema) => schema.required("Please select a self collection location"),
				otherwise: (schema) => schema.nullable(),
			}),
			delivery_recipient: yupHelper.fullname().when(["cert_type"], {
				is: (cert_type) => cert_type === "physical",
				then: (schema) => schema.required("Please enter a delivery recipient"),
				otherwise: (schema) => schema.nullable(),
			}),
			delivery_contact: yupHelper.mobile().when(["cert_type"], {
				is: (cert_type) => cert_type === "physical",
				then: (schema) => schema.required("Please enter a delivery contact"),
				otherwise: (schema) => schema.nullable(),
			}),
			delivery_email: yup
				.string()
				.email()
				.when(["cert_type"], {
					is: (cert_type) => cert_type === "physical",
					then: (schema) => schema.required("Please enter a delivery email"),
					otherwise: (schema) => schema.nullable(),
				}),
			delivery_address_line: yup.string().when(["cert_type", "cert_delivery_method"], {
				is: (cert_type, cert_delivery_method) =>
					cert_type === "physical" && cert_delivery_method === "Mail",
				then: (schema) => schema.required("Please enter a address "),
				otherwise: (schema) => schema.nullable(),
			}),
			delivery_address_unit: yup.string().when(["cert_type", "cert_delivery_method"], {
				is: (cert_type, cert_delivery_method) =>
					cert_type === "physical" && cert_delivery_method === "Mail",
				then: (schema) => schema.required("Please enter unit number"),
				otherwise: (schema) => schema.nullable(),
			}),
			delivery_address_block: yup.string().when(["cert_type", "cert_delivery_method"], {
				is: (cert_type, cert_delivery_method) =>
					cert_type === "physical" && cert_delivery_method === "Mail",
				then: (schema) => schema.required("Please enter a block number"),
				otherwise: (schema) => schema.nullable(),
			}),
			delivery_address_postal: yup.string().when(["cert_type", "cert_delivery_method"], {
				is: (cert_type, cert_delivery_method) =>
					cert_type === "physical" && cert_delivery_method === "Mail",
				then: (schema) => schema.required("Please enter  postal code"),
				otherwise: (schema) => schema.nullable(),
			}),
			delivery_country: yup.string().when(["cert_type", "cert_delivery_method"], {
				is: (cert_type, cert_delivery_method) =>
					cert_type === "physical" && cert_delivery_method === "Mail",
				then: (schema) => schema.required("Please enter country"),
				otherwise: (schema) => schema.nullable(),
			}),
		};
		return yup.object(baseSchema);
	}, []);

	const checkboxTNC = useRef(null);
	const canvasRef = useRef(null);

	const {
		register,
		formState: { errors },
		getValues,
		setValue,
		handleSubmit,
		watch,
		reset,
	} = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			// cert_type: checkoutInfo?.is_purchase_hardcopy ? "physical" : checkoutInfo?.cert_type,
			cert_type: "digital",
			cert_folder_selection: checkoutInfo?.cert_folder_selection,
			cert_delivery_method: checkoutInfo?.cert_delivery_method,
			self_collection_location: checkoutInfo?.self_collection_location,
			collection_location: checkoutInfo?.collection_location,
			delivery_recipient: checkoutInfo?.delivery_recipient,
			delivery_email: checkoutInfo?.delivery_email,
			delivery_contact: checkoutInfo?.delivery_contact,
			delivery_address_line: checkoutInfo?.delivery_address_line,
			delivery_address_unit: checkoutInfo?.delivery_address_unit,
			delivery_address_block: checkoutInfo?.delivery_address_block,
			delivery_address_postal: checkoutInfo?.delivery_address_postal,
			delivery_country: checkoutInfo?.delivery_country || "Singapore",
		},
	});

	const cert_type = watch("cert_type");
	const cert_delivery_method = watch("cert_delivery_method");

	// Load cached form data on component mount
	useEffect(() => {
		if (!cert_request_id) return;

		const cachedData = getCachedFormData();
		if (cachedData && cachedData.cert_request_id === cert_request_id) {
			// Cache is valid, restore form data
			console.log("Loading cached form data for cert_request_id:", cert_request_id);

			// Restore form values
			Object.keys(cachedData.formData).forEach((key) => {
				if (cachedData.formData[key] !== undefined) {
					setValue(key, cachedData.formData[key]);
				}
			});

			// Restore component state
			if (cachedData.selectedFolder) {
				setSelectedFolder(cachedData.selectedFolder);
			}
			if (cachedData.selectedCollectionLocation) {
				setSelectedCollectionLocation(cachedData.selectedCollectionLocation);
			}
			if (cachedData.paymentmethod) {
				setPaymentmethod(cachedData.paymentmethod);
			}
		} else {
			// Cache is invalid or doesn't exist, clear it
			if (cachedData) {
				console.log("Clearing invalid cache - cert_request_id mismatch");
				clearCachedFormData();
			}
		}
	}, [cert_request_id, checkoutInfo, setValue, reset]);

	const pricePreviewQuery = useQuery({
		enabled: Boolean(cert_request_id),
		keepPreviousData: true,
		queryKey: [
			"price-preview",
			cert_request_id,
			selectedFolder?.id,
			cert_type || "digital",
			cert_delivery_method,
		],
		queryFn: () => {
			return generalApi
				.calculateProfessionalCertPrice({
					cert_request_id,
					// we default for digital cert , so that the default price should will be based on digital cert
					// they don't have selec the cert type first
					cert_type: cert_type || "digital",
					cert_folder_selection: selectedFolder?.id,
					cert_delivery_method,
				})
				.then((response) => {
					return response;
				});
		},
	});

	function selectPaymentmethod(event) {
		const selectedMethod = event.currentTarget.dataset.method;
		setPaymentmethod(selectedMethod);
		console.log("clicked-", selectedMethod);
	}

	const paymentMutation = useMutation({
		mutationFn: async () => {
			const dataUrl = canvasRef.current.toDataURL("image/png");
			if (checkboxTNC.current.checked === false) {
				throw new Error("Please agree to the terms and conditions");
			}

			if (canvasRef.current.isEmpty()) {
				throw new Error("Please sign before continue");
			}

			if (cert_type === "physical") {
				if (!cert_delivery_method) {
					throw new Error("Please select a delivery method");
				}
			}

			if (cert_type === "physical" && cert_delivery_method === "Self Collection") {
				if (!getValues("self_collection_location")) {
					throw new Error("Please select a self collection location");
				}
			}

			const submissionData = {
				...getValues(),
				signature: dataUrl,
				cert_request_id: cert_request_id,
				folder_id: selectedFolder?.id,
			};

			/**
			 *  if not amount then directly initial the order
			 */
			if (!pricePreviewQuery.data.total_amount) {
				return await generalApi
					.epaymentForCert(submissionData)
					.then((response) => {
						if (response.status == "completed") {
							console.log("response", response);
							// Clear cache on successful completion
							clearCachedFormData();
							console.log("Clearing cache after successful checkout");

							// window.location.href = data.redirect_url;
							navigate("/professional-cert/checkout/success");
							Swal.fire({
								title: "Success",
								html: "Your order has been created successfully",
								icon: "success",
								confirmButtonText: "View Certificate",
							}).then((result) => {
								if (result.isConfirmed) {
									console.log(response.data.redirect_url);
									window.location.href = response.data.redirect_url;
								} else {
									// navigate("/profile");
								}
							});
						} else {
							Swal.fire({
								title: "Error",
								html: "Something went wrong - unexpected error",
								icon: "error",
							});
						}
					})
					.catch((error) => {
						Swal.fire({
							title: "Error",
							html: "Something went wrong",
							icon: "error",
						});
						throw error;
					});
			}

			/**
			 * Configure and intial the payment method
			 */
			if (paymentmethod == "creditcard") {
				const flow = new CreditCardFlow({
					paymentAmount: pricePreviewQuery.data.total_amount,
					data: submissionData,
					initializeEpayment: generalApi.epaymentForCert,
					createOmiseCharge: generalApi.createOmiseChargeForCert,
				});
				await flow.processPayment();
			} else if (paymentmethod == "paynow") {
				const flow = new PayNowFlow({
					paymentAmount: pricePreviewQuery.data.total_amount,
					data: submissionData,
					initializeEpayment: generalApi.epaymentForCert,
					createOmiseCharge: generalApi.createOmiseChargeForCert,
				});
				await flow.processPayment().then((epaymentResult) => {
					console.log("Clearing cache before navigating to PayNow");

					navigate(
						{
							pathname: "/professional-cert/checkout/omise/paynow",
						},
						{
							state: {
								payment_id: epaymentResult.data.payment_id,
								payment_paynow_qr: epaymentResult.data.payment_paynow_qr,
							},
						},
					);
				});
			} else if (paymentmethod == "applepay") {
				const flow = new ApplePayFlow({
					label: "Event Checkout",
					paymentAmount: pricePreviewQuery.data.total_amount,
					data: submissionData,
					initializeEpayment: generalApi.epaymentForCert,
					createOmiseCharge: generalApi.createOmiseChargeForCert,
				});
				await flow.processPayment();
			} else if (paymentmethod == "googlepay") {
				const flow = new GooglePayFlow({
					paymentAmount: pricePreviewQuery.data.total_amount,
					data: submissionData,
					initializeEpayment: generalApi.epaymentForCert,
					createOmiseCharge: generalApi.createOmiseChargeForCert,
				});
				await flow.processPayment();
			}
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			Swal.fire({
				title: "Error",
				html: errorMessage,
				icon: "error",
			});
		},
	});

	const handleCheckout = handleSubmit(
		async () => {
			if (paymentMutation.isLoading) {
				return;
			}

			// Save current form state to cache before proceeding to checkout
			const cacheData = {
				cert_request_id,
				formData: getValues(),
				selectedFolder,
				selectedCollectionLocation,
				paymentmethod,
				timestamp: Date.now(),
			};
			setCachedFormData(cacheData);
			console.log("Saving form data to cache before checkout");

			paymentMutation.mutate();
			return;
		},
		(errors) => {
			console.log("validation errors", errors);
		},
	);

	// useEffect(() => {
	// 	if (!selectedFolder && checkoutInfo?.folder_options?.length) {
	// 		setSelectedFolder(checkoutInfo.folder_options.find((folder) => folder.is_default));
	// 		setValue(
	// 			"cert_folder_selection",
	// 			checkoutInfo.folder_options.find((folder) => folder.is_default).id,
	// 		);
	// 	}
	// }, [checkoutInfo?.folder_options]);

	const checkoutError = useMemo(() => {
		if (!cert_type) {
			return "Do you want to receive a physical certificate?";
		}

		if (cert_type === "physical") {
			if (!cert_delivery_method) {
				return "Please select a delivery method";
			}
		}

		return null;
	}, [cert_type, cert_delivery_method]);

	return (
		<>
			<h2 className="text-center pb-4">
				{checkoutInfo?.is_purchase_hardcopy ? "Purchase Hardcopy" : "Checkout"}
			</h2>
			<div className="row">
				<div className="col-md-8">
					<div className={styles.root}>
						{checkoutInfo?.is_purchase_hardcopy ? null : (
							<div className="d-flex justify-content-between align-items-center">
								<div className={"fs30 font-weight-bold text-color-dark"}>Digital Certificate</div>
								<div className={"fs30 font-weight-bold text-color-dark"}>
									<PriceTag>
										<span className="fs20">
											{checkoutInfo?.digital_cert_price
												? `SGD ${checkoutInfo?.digital_cert_price}`
												: "Free"}
										</span>
									</PriceTag>
								</div>
							</div>
						)}

						<FormGroup>
							<Label>Display Name</Label>
							<input
								readOnly
								className={"form-control"}
								placeholder="Display Name"
								value={checkoutInfo?.display_name}
							/>
						</FormGroup>

						<div className="fs18 text-color-dark">
							Following Course(s) will be printed on the certificate:
						</div>
						<ul>
							{checkoutInfo?.courses?.map((course) => (
								<li key={course.id}>
									<div className="fs16 mt-2">
										<div>{course.course_title} </div>
										<div className="text-muted">{course.course_ref}</div>
										<div className="text-muted"> Completed on {course.courserun_course_to}</div>
										<div className="text-muted">Trainer: {course.trainer_name}</div>
									</div>
								</li>
							))}
						</ul>
					</div>

					{/* {
						<div className={styles.root}>
							<div className="d-flex justify-content-between align-items-center">
								<div className={"fs30 font-weight-bold text-color-dark"}>
									Do you want to receive a physical certificate?
								</div>
							</div>
							<div className={"fs30 font-weight-bold text-color-dark"}></div>
							<div className="fs18 text-color-dark mt-2">
								Select the type of certificate you want to receive:
							</div>
							<div className="d-flex gap-1 align-items-stretch flex-column flex-md-row mt-4">
								<div style={{ flex: 1 }}>
									<SelectionBox
										title="Yes"
										description={
											<span className="d-flex flex-column align-items-center">
												<span>Physical Certificate + Hot Stamping + Lamination</span>
												<PriceTag>
													<span>SGD {checkoutInfo.physical_cert_price}</span>
												</PriceTag>
											</span>
										}
										onChange={() => setValue("cert_type", "physical")}
										isSelected={cert_type === "physical"}
										blur={Boolean(cert_type) && cert_type !== "physical"}
									/>
								</div>
								<div style={{ flex: 1 }}>
									<SelectionBox
										title="No"
										description={
											<span className="d-flex flex-column align-items-center">
												<span>No , I only want a digital certificate</span>
												<PriceTag>Free</PriceTag>
											</span>
										}
										onChange={() => {
											if (checkoutInfo.is_purchase_hardcopy) {
												return;
											}
											setValue("cert_type", "digital");
										}}
										isSelected={cert_type === "digital"}
										blur={Boolean(cert_type) && cert_type !== "digital"}
									/>
								</div>
							</div>
						</div>
					} */}
					{/* <div className={"position-relative"}>
						{checkoutInfo.folder_options?.length && cert_type !== "digital" ? (
							<>
								<div className={classNames(styles.root)}>
									<div className="d-flex justify-content-start align-items-center gap-2">
										<div className={"fs30 font-weight-bold text-color-dark"}>Folder Selection</div>
										<Badge color="#00AB84" bdrs={"sm"} size="xl" tt="none">
											{selectedFolder ? `${selectedFolder.name}` : "No Folder Selected"}
										</Badge>
									</div>
									<div className={styles.folderSelectionRow}>
										<SelectionBoxWithImage
											className
											title="No Folder"
											galleryID="no-folder"
											imageSize={200}
											price="Free"
											onChange={() => {
												setSelectedFolder(null);
												setValue("cert_folder_selection", null);
											}}
											isSelected={!selectedFolder}
											image={
												<div
													className={classNames(
														styles.previewImage,
														"d-flex justify-content-center align-items-center",
													)}
													style={{
														backgroundColor: "#eee",
														width: 200,
														height: 200,
													}}>
													<FolderOffSvg
														width={100}
														height={100}
														style={{
															fill: "#efefef",
															stroke: "gray",
															width: 100,
															height: 100,
														}}
													/>
												</div>
											}
										/>

										{checkoutInfo.folder_options.map((folder, index) => {
											return (
												<SelectionBoxWithImage
													imageSize={200}
													key={`folder-${index}`}
													galleryID={`folder-${index}`}
													title={folder.name}
													price={folder.price ? `$${folder.price}` : "Free"}
													onChange={() => {
														setSelectedFolder(folder);
														setValue("cert_folder_selection", folder.id);
													}}
													isSelected={selectedFolder?.id === folder.id}
													image={folder.image_url}
												/>
											);
										})}
									</div>
								</div>

								<div className={styles.root}>
									<div className={"fs30 font-weight-bold text-color-dark"}>Delivery Method</div>
									<div className={"fs18 text-color-dark"}>
										Select the delivery method for your physical certificate:
									</div>

									<div className="d-flex gap-1 align-items-stretch flex-column flex-md-row mt-4">
										<div style={{ flex: 1 }}>
											<SelectionBox
												title="Self Collection"
												onChange={() => setValue("cert_delivery_method", "Self Collection")}
												isSelected={cert_delivery_method === "Self Collection"}
												description={
													<span className="d-flex justify-content-center align-items-center flex-wrap gap-1">
														<span>Collect Certificate at our office</span>
														<PriceTag>Free</PriceTag>
													</span>
												}
												blur={
													Boolean(cert_delivery_method) &&
													cert_delivery_method !== "Self Collection"
												}
											/>
										</div>
										<div style={{ flex: 1 }}>
											<SelectionBox
												title="Mail Delivery"
												onChange={() => setValue("cert_delivery_method", "Mail")}
												isSelected={cert_delivery_method === "Mail"}
												description={
													<span className="d-flex justify-content-center align-items-center flex-wrap gap-1">
														<span>Doorstep Delivery</span>
														<PriceTag>SGD 10</PriceTag>
													</span>
												}
												blur={Boolean(cert_delivery_method) && cert_delivery_method !== "Mail"}
											/>
										</div>
									</div>

									<div className={classNames("position-relative mt-4")}>
										{cert_delivery_method === "Mail" && (
											<>
												<Alert
													color={CERT_GREEN_COLOR}
													title="📫 We will send the physical certificate to the following address:"></Alert>
											</>
										)}

										{cert_delivery_method === "Self Collection" && (
											<>
												<Alert
													color={CERT_GREEN_COLOR}
													title="📫 We will send the physical certificate to the following address:"></Alert>
											</>
										)}

										<Form className="mt-4">
											<div className="row">
												<div className="col-12">
													<FormGroup>
														<Label>Recipient Name*</Label>
														<input
															className={"form-control " + (errors.nric ? "is-invalid" : "")}
															{...register("delivery_recipient")}
															placeholder="Name"
														/>
														<FormFeedback>
															{errors.delivery_recipient ? errors.delivery_recipient.message : null}
														</FormFeedback>
													</FormGroup>
												</div>
											</div>
											<div className="row">
												<div className="col-md-6">
													<FormGroup>
														<Label>Email*</Label>
														<input
															className={
																"form-control " + (errors.delivery_email ? "is-invalid" : "")
															}
															{...register("delivery_email")}
															placeholder="Email"
														/>
														<FormFeedback>{errors.name ? errors.name.message : null}</FormFeedback>
													</FormGroup>
												</div>
												<div className="col-md-6">
													<FormGroup>
														<Label>Phone Number*</Label>
														<input
															className={
																"form-control " + (errors.delivery_contact ? "is-invalid" : "")
															}
															{...register("delivery_contact")}
															placeholder="Phone Number"
														/>
														<FormFeedback>
															{errors.delivery_contact ? errors.delivery_contact.message : null}
														</FormFeedback>
													</FormGroup>
												</div>
											</div>

											{cert_delivery_method === "Mail" && (
												<>
													<div className="row">
														<div className="col-12">
															<FormGroup>
																<Label>Address*</Label>
																<input
																	className={
																		"form-control " +
																		(errors.delivery_address_line ? "is-invalid" : "")
																	}
																	{...register("delivery_address_line")}
																	placeholder="Address"
																/>
																<FormFeedback>
																	{errors.delivery_address_line
																		? errors.delivery_address_line.message
																		: null}
																</FormFeedback>
															</FormGroup>
														</div>
													</div>
													<div className="row">
														<div className="col-md-6">
															<FormGroup>
																<Label>Block/Building</Label>
																<input
																	className={
																		"form-control " +
																		(errors.delivery_address_block ? "is-invalid" : "")
																	}
																	{...register("delivery_address_block")}
																	placeholder="Block/Building"
																/>
																<FormFeedback>
																	{errors.delivery_address_block
																		? errors.delivery_address_block.message
																		: null}
																</FormFeedback>
															</FormGroup>
														</div>
														<div className="col-md-6">
															<FormGroup>
																<Label>Unit</Label>
																<input
																	className={
																		"form-control " +
																		(errors.delivery_address_unit ? "is-invalid" : "")
																	}
																	{...register("delivery_address_unit")}
																	placeholder="Unit"
																/>
																<FormFeedback>
																	{errors.delivery_address_unit
																		? errors.delivery_address_unit.message
																		: null}
																</FormFeedback>
															</FormGroup>
														</div>
														<div className="col-md-6">
															<FormGroup>
																<Label>Postal Code</Label>
																<input
																	className={
																		"form-control " +
																		(errors.delivery_address_postal ? "is-invalid" : "")
																	}
																	{...register("delivery_address_postal")}
																	placeholder="Postal Code"
																/>
																<FormFeedback>
																	{errors.delivery_address_postal
																		? errors.delivery_address_postal.message
																		: null}
																</FormFeedback>
															</FormGroup>
														</div>
														<div className="col-md-6">
															<FormGroup>
																<Label>Country</Label>
																<input
																	className={
																		"form-control " + (errors.delivery_country ? "is-invalid" : "")
																	}
																	{...register("delivery_country")}
																	placeholder="Country"
																/>
															</FormGroup>
														</div>
													</div>
												</>
											)}

											{cert_delivery_method === "Self Collection" && (
												<>
													<div className="row">
														<div className="col-12">
															<div>
																<Label>Collection Location</Label>
																<br />
																<Label className="text-muted">
																	Please indicate your preferred location
																</Label>
																<div className={styles.folderSelectionRow}>
																	{checkoutInfo.collection_location_options.map(
																		(location, index) => {
																			return (
																				<SelectionBoxWithImage
																					key={`location-${index}`}
																					galleryID={`location-${index}`}
																					title={"📍" + location.title}
																					description={location.description}
																					onChange={() => {
																						setSelectedCollectionLocation(location);
																						setValue("self_collection_location", location.id);
																					}}
																					isSelected={
																						selectedCollectionLocation?.id === location.id
																					}
																					image={location.image_url}
																					imageSize={200}
																				/>
																			);
																		},
																	)}
																</div>
															</div>
														</div>
													</div>
												</>
											)}
										</Form>

										{cert_delivery_method === null || cert_delivery_method === undefined ? (
											<div
												className="position-absolute top-0 start-0 w-100 h-100 rounded"
												style={{
													backgroundColor: "rgba(0, 0, 0, 0.5)",
													backdropFilter: "blur(10px)",
												}}>
												<div className="d-flex justify-content-center align-items-center h-100">
													<div className="fs20 font-weight-bold text-white text-center">
														Please select your preferred delivery method above
													</div>
												</div>
											</div>
										) : null}
									</div>
								</div>
							</>
						) : null}

						{cert_type === null || cert_type === undefined ? (
							<div
								className="position-absolute top-0 start-0 w-100 h-100 rounded"
								style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", backdropFilter: "blur(10px)" }}>
								<div className="d-flex justify-content-center align-items-center h-100">
									<div className="fs20 font-weight-bold text-white text-center">
										Please select an option above
									</div>
								</div>
							</div>
						) : null}
					</div> */}

					{/* Endorsement & Payment*/}
					{!checkoutError && (
						<div className={styles.root}>
							<div className={"fs30 font-weight-bold text-color-dark"}>Endorsement & Payment</div>
							<Text id="terms-and-conditions">Terms & Conditions</Text>
							<Divider my={10} />
							<TNC accordionOpen={accordionOpen} />
							<Row className="pb-3">
								<Col>
									<div className="pt-3">
										<div className="pb-2">
											<b className="text-color-dark">Your Signature</b>
											<Text
												ml={20}
												td="underline"
												fw="bold"
												component="a"
												style={{ cursor: "pointer" }}
												c={CERT_GREEN_COLOR}
												onClick={(e) => {
													e.preventDefault();
													canvasRef.current.clear();
												}}>
												Clear
											</Text>
										</div>
										<SignatureCanvas
											ref={canvasRef}
											clearOnResize={false}
											canvasProps={{
												height: 200,
												className: "sigCanvas border",
												style: { width: "100%", maxWidth: "500px" },
											}}
										/>
									</div>

									<div className="pt-2">
										<label htmlFor="tnc-checkbox">
											<Checkbox
												size={20}
												id="tnc-checkbox"
												ref={checkboxTNC}
												label={
													<Text size="sm">
														I acknowledge that I have read and agree to the above
														<a
															href="#terms-and-conditions"
															className="ml-2 text-fca-blue text-underline cursor-pointer">
															terms and conditions.
														</a>
													</Text>
												}
											/>
										</label>
									</div>
								</Col>
							</Row>
							<div>
								{pricePreviewQuery.data?.total_amount ? (
									<>
										<div className="mt-4 select-payment-method">
											<span className="fs18 text-color-dark">Select One Payment Method</span>
										</div>
										<div className={classNames(styles.paymentGrid, "mx-auto my-2")}>
											<PaymentMethodCard
												onClick={selectPaymentmethod}
												title="Paynow"
												description="Pay the balance amount using Paynow"
												logo={PayNowLogo}
												active={paymentmethod === "paynow"}
												paymentMethod="paynow"
											/>
											<PaymentMethodCard
												onClick={selectPaymentmethod}
												paymentMethod="creditcard"
												title="Credit Card"
												description="Pay the balance amount using Credit Card"
												logo={CreditCardLogo}
												active={paymentmethod === "creditcard"}
											/>
										</div>
									</>
								) : null}

								{checkoutError ? (
									<div className="d-flex align-items-center gap-2 justify-content-center alert alert-danger rounded rounded-sm">
										<span>{checkoutError}</span>
									</div>
								) : (
									<div
										className="d-none d-md-flex"
										style={{
											alignItems: "flex-end",
											justifyContent: "flex-end",
											width: "100%",
										}}>
										<ThemedButton
											loading={paymentMutation.isLoading}
											disabled={
												paymentMutation.isLoading || paymentMutation.isSuccess || checkoutError
											}
											onClick={handleCheckout}>
											<div className="d-flex justify-content-center gap-1">
												{paymentMutation.isLoading ? <Spinner size={"sm"} /> : ""}
												<span>
													{pricePreviewQuery.data?.total_amount
														? "Proceed to Payment"
														: "Complete Order"}
												</span>
											</div>
										</ThemedButton>
									</div>
								)}
							</div>
						</div>
					)}
				</div>
				<div className="col-md-4">
					<CheckoutSidebar
						checkoutError={checkoutError}
						calculatedPrice={pricePreviewQuery.data}
						disabled={!pricePreviewQuery.isFetched}
						loading={
							paymentMutation.isLoading ||
							pricePreviewQuery.isLoading ||
							pricePreviewQuery.isFetching
						}
						onCheckout={handleCheckout}
					/>
				</div>
			</div>
		</>
	);
};

function PaymentMethodCard({ onClick, title, description, logo, active, paymentMethod }) {
	return (
		<div
			className={classNames(styles.paymentMethodCard, active ? "active" : "")}
			onClick={onClick}
			data-method={paymentMethod}>
			{paymentMethod === "applepay" && (
				<div>
					<apple-pay-button buttonstyle="black" type="plain" locale="en-US"></apple-pay-button>
				</div>
			)}
			{logo ? (
				<img src={logo} style={{ height: "35px", objectFit: "contain" }} className="my-2" />
			) : null}

			<div className="d-flex flex-column align-items-center">
				<span className="title">{title}</span>
				<span className="description">{description}</span>
			</div>
		</div>
	);
}

const SelectionBox = ({ title, description, onChange, isSelected, blur }) => {
	return (
		<div
			onClick={onChange}
			className={classNames("card rounded shadow-none p-3 text-center w-100 h-100 mb-0")}
			style={{
				height: "100%",
				cursor: "pointer",
				border: isSelected ? "2px solid #00AB84" : "2px solid #ababab",
				transition: "all 0.3s ease",
				filter: blur ? "opacity(0.3)" : "none",
			}}>
			<h5
				className="mb-0"
				style={{
					color: isSelected ? "#00AB84" : "black",
				}}>
				{title}
			</h5>
			{description ? <span className="description text-muted mt-1">{description}</span> : null}
		</div>
	);
};

const SelectionBoxWithImage = ({
	title,
	price,
	description,
	onChange,
	isSelected,
	image,
	galleryID,
	imageSize = 400,
}) => {
	return (
		<div
			className={classNames(
				styles.folderSelectionItem,
				"my-4 p-md-4",
				isSelected ? styles.selected : "",
				"d-flex flex-column justify-content-between",
			)}>
			<div className="flex-grow-1">
				<div className="d-flex justify-content-between align-items-start" onClick={onChange}>
					<div className="fs18">{title}</div>
					{price ? <PriceTag>{price}</PriceTag> : null}
				</div>
				{description ? <div className="fs15">{description}</div> : null}
			</div>

			{typeof image === "object" ? image : null}
			{typeof image === "string" ? (
				<PreviewImage
					imageClass={styles.previewImage}
					galleryID={galleryID}
					imageStyle={{
						width: imageSize,
						height: imageSize,
					}}
					image={{
						url: image,
						alt: title,
						width: imageSize,
						height: imageSize,
						thumbnailURL: image,
						largeURL: image,
					}}
				/>
			) : null}

			<div className="mt-4">
				<Button
					disabled={isSelected}
					variant="outline"
					color="#00AB84"
					style={{ width: "100%" }}
					onClick={onChange}>
					Select
				</Button>
			</div>
		</div>
	);
};

const PriceTag = ({ children }) => {
	return (
		<div
			className="fs15 rounded px-2 py-1 font-weight-bold text-color-dark d-inline-block text-nowrap"
			style={{
				backgroundColor: "#00AB84",
				color: "white",
			}}>
			{children}
		</div>
	);
};
